import { API_BASE_URL } from '../../api/constants.js';

/**
 * RegistrationApi — dedicated Symfony API client for the registration flow.
 * Deliberately does NOT extend the shared `BaseApi`/`apiRequest` (`api/utils.js`):
 * that client attaches the legacy PHPSESSID `token` header and hard-navigates to
 * `/authorization/login/` on any 401 (`api/authSession.js`). Neither applies here
 * — every caller is anonymous, and a 4xx (duplicate email, weak password,
 * mismatched confirmation, invalid/expired confirmation token, 429 rate limit)
 * is a normal inline form state, not a reason to navigate the visitor away.
 *
 * All three routes (`register`, `register/confirm`, `register/resend`) are on
 * the Symfony `auth_public` firewall — no credential of any kind is ever sent.
 */
async function request(path, { method = 'GET', body } = {}) {
	const response = await fetch(`${API_BASE_URL}${path}`, {
		method,
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		...(body !== undefined && { body: JSON.stringify(body) })
	});

	let data = null;
	try {
		data = await response.json();
	} catch {
		data = null;
	}

	if (!response.ok) {
		const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
		error.response = { data, status: response.status };
		throw error;
	}

	return { data };
}

class RegistrationApi {
	/**
	 * POST /api/v1/auth/register — 201 on success, account unconfirmed, no
	 * token/session issued. 409 for a duplicate email, 422 for a validation
	 * failure (weak password, mismatched confirmation, invalid email), 429
	 * past 5 attempts/hour/IP.
	 */
	static register({ email, password, passwordConfirmation }) {
		return request('/auth/register', { method: 'POST', body: { email, password, passwordConfirmation } });
	}

	/**
	 * GET /api/v1/auth/register/confirm?token=... — 200 on success (no
	 * token/session issued — the account can now log in, nothing more). 400
	 * for an invalid, expired, or already-used token (all three collapse to
	 * the same response by design — which one it was would leak whether an
	 * account exists behind that token). 429 past 5 attempts/hour/IP.
	 */
	static confirmEmail(token) {
		return request(`/auth/register/confirm?token=${encodeURIComponent(token)}`);
	}

	/**
	 * POST /api/v1/auth/register/resend — always 200 regardless of whether the
	 * address exists or is already confirmed (deliberate email-enumeration
	 * protection — never surface which case it was). 429 past 5/hour/IP.
	 */
	static resendConfirmation(email) {
		return request('/auth/register/resend', { method: 'POST', body: { email } });
	}
}

export default RegistrationApi;
